import React, { Fragment, useMemo } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { Box, Chip, Grid, Typography } from '@mui/material';
import { DateFormats, TimeFormats } from '@uplift/constants';
import { useTimezone } from '@uplift/contexts';
import { useL10n } from '@uplift/hooks';
import { CalendarDateService } from '@uplift/services';
import { AppointmentAssessments } from './components/appointment-assessments';
import { LabelColonValue, LabelColonValueSmall } from './components/label-colon-value.component';
import { SectionContainer } from './components/section-container.component';
import { ClinicalNotesModalStateType } from './hooks/use-clinical-notes-modal.hook';
import { useFollowUpAppointmentDate } from './hooks/use-next-appointment-date.hook';
import { useStyles } from './styles';
import { Appointment, AppointmentType, PsychiatryNote, psychiatryNoteFormConfigByKey, Session, sessionParticipantOptionsDict, sessionServiceOptionsDict } from '@uplift/api';
import { Markdown } from '../../../Markdown';
import { usePatientAllergiesQuery, usePatientMedicationQuery, useSessionCptCodesQuery, useSessionVerificationLocationQuery, useUserDisplayNameQuery, useUserProfileQuery, useUserQuery } from '@uplift/queries';
import { AddendumContainer } from './components/addendum-container.component';


type ClinicalNotesModalRawProps = {
  state: ClinicalNotesModalStateType;
  session: Session;
  appointment: Appointment;
  patientId: string;
  appointmentId?: string;
};

type RenderProgressFunctionType = (sessionNotesView: ClinicalNotesModalStateType['sessionNotesView'], followUpJSX: React.ReactNode) => React.ReactNode;
type RenderRiskAssessmentContentFunctionType = (sessionNotesView: ClinicalNotesModalStateType['sessionNotesView']) => React.ReactNode;


export const SessionDetailsSection: React.FC<ClinicalNotesModalRawProps> = ({ state, session }) => {
  const l10n = useL10n();
  const timezone = useTimezone();
  const { data: verificationLocation } = useSessionVerificationLocationQuery(session.id);
  const { type, appointment, sessionNotesView, psychiatryNote } = state;
  const { startDate, endDate } = React.useMemo(() => {
    if (type === AppointmentType.therapy && sessionNotesView) {
      return {
        startDate: CalendarDateService.date(sessionNotesView?.notes?.startTime ?? appointment?.fromDatetime).tz(timezone),
        endDate: CalendarDateService.date(sessionNotesView?.notes?.endTime ?? appointment?.toDatetime).tz(timezone),
      };
    } else if (type === AppointmentType.psychiatry && psychiatryNote) {
      return {
        startDate: CalendarDateService.date(psychiatryNote?.startTime ?? appointment?.fromDatetime).tz(timezone),
        endDate: CalendarDateService.date(psychiatryNote?.endTime ?? appointment?.toDatetime).tz(timezone),
      };
    }

    return {
      startDate: CalendarDateService.date(appointment?.fromDatetime).tz(timezone),
      endDate: CalendarDateService.date(appointment?.toDatetime).tz(timezone),
    };
  }, [ type, psychiatryNote, sessionNotesView, appointment, timezone ]);
  const dateOfService = startDate.format(DateFormats['M/D/YY']);
  const formatttedStartTime = startDate.format(TimeFormats['h:mm']);
  const formattedEndTime = endDate.format(TimeFormats['h:mm']);
  const meridiem = endDate.format('a').replace(/(.{1})/g, '$1.');
  const minutesDiff = endDate.diff(startDate, 'minutes');
  const duration = `${formatttedStartTime}–${formattedEndTime} ${meridiem}, ${minutesDiff} minutes`;
  const { data: providerDisplayName } = useUserDisplayNameQuery(state.appointment.provider.id);
  const providerName = providerDisplayName?.fullLegalName ?? '';
  const localSubmissionTime = CalendarDateService.date(session.submittedAt).tz(timezone);
  const dateOfSubmission = localSubmissionTime.format(DateFormats['MM/DD/YY']);
  const submissionTime = localSubmissionTime.format(TimeFormats['h:mm']);
  const submissionMeridiem = localSubmissionTime.format('a').replace(/(.{1})/g, '$1.');
  const { data: cptCodes } = useSessionCptCodesQuery(session, { suspense: true });

  const location = React.useMemo(() => {
    if (verificationLocation) {
      return `${sessionServiceOptionsDict[session.serviceType]}; verified at ${verificationLocation}`;
    }
    return sessionServiceOptionsDict[session.serviceType];
  }, [ session.serviceType, verificationLocation ]);

  return (
    <SectionContainer title={l10n.clinicalNotes.details}>
      <Grid container>
        <Grid
          size={{
            xs: 12,
            sm: 6
          }}
        >
          <LabelColonValueSmall label={l10n.clinicalNotes.dateOfService} value={dateOfService} />
        </Grid>
        <Grid
          size={{
            xs: 12,
            sm: 6
          }}
        >
          <LabelColonValueSmall label={l10n.clinicalNotes.provider} value={providerName} />
        </Grid>
        <Grid
          size={{
            xs: 12,
            sm: 6
          }}
        >
          <LabelColonValueSmall label={l10n.clinicalNotes.duration} value={duration} />
        </Grid>
        {session.participation && (
          <Grid
            size={{
              xs: 12,
              sm: 6
            }}
          >
            <LabelColonValueSmall label={l10n.clinicalNotes.participants} value={sessionParticipantOptionsDict[session.participation]} />
          </Grid>
        )}
        {session.serviceType && (
          <Grid
            size={{
              xs: 12,
              sm: 6
            }}
          >
            <LabelColonValueSmall label={l10n.clinicalNotes.location} value={location} />
          </Grid>
        )}
        <Grid
          size={{
            xs: 12,
            sm: 6
          }}
        >
          <LabelColonValueSmall label={l10n.clinicalNotes.cptCode} value={cptCodes?.map(c => c.code).join(', ') ?? ''} />
        </Grid>
      </Grid>
      <Box pt={3}>
        <Typography variant="body2" color="textPrimary">
          {localSubmissionTime.isValid()
            ? l10n.clinicalNotes.submittedByOn(providerName, providerDisplayName?.suffix ?? '', dateOfSubmission, submissionTime, submissionMeridiem)
            : 'Session is not submitted yet'
          }
        </Typography>
      </Box>
    </SectionContainer>
  );
};


const ClinicalNoteContent: React.FC<ClinicalNotesModalRawProps> = ({ session, appointment, state, patientId }) => {
  const l10n = useL10n();
  const { sessionNotesView } = state;
  const { dateOfNextAppointment, loading } = useFollowUpAppointmentDate(appointment.id);
  const { data: patient } = useUserQuery(appointment.patientId, { suspense: true });
  const followUpDate = useMemo(() => {
    if (loading) return '';
    if (!dateOfNextAppointment) return l10n.clinicalNotes.clientScheduleNextAppointment;
    return l10n.clinicalNotes.nextAppointment(
      dateOfNextAppointment.date,
      dateOfNextAppointment.time,
      dateOfNextAppointment.meridiem
    );
  }, [ dateOfNextAppointment, l10n.clinicalNotes, loading ]);
  const followUpJSX = <LabelColonValue mb={0} label={l10n.clinicalNotes.followUp} value={followUpDate} />;

  const renderProgress: RenderProgressFunctionType = (sessionNotesView, followUpJSX) => {
    if (sessionNotesView?.notes.isInitialSession) {
      return (
        <SectionContainer title={l10n.clinicalNotes.plan}>
          <AppointmentAssessments assessments={sessionNotesView?.notes.assessments ?? []} mb={2} />
          <LabelColonValue my={0} label={l10n.clinicalNotes.goals} value={sessionNotesView?.initialProgressNote.goal} />
          <LabelColonValue my={2} label={l10n.clinicalNotes.objectives} value={sessionNotesView?.initialProgressNote.objective} />
          <LabelColonValue my={2} label={l10n.clinicalNotes.frequencyOfTreatment} value={sessionNotesView?.initialProgressNote.treatmentFrequency} />
          <LabelColonValue my={2} label={l10n.clinicalNotes.referrals} value={sessionNotesView?.initialProgressNote.referrals} />
          {followUpJSX}
        </SectionContainer>
      );
    } else {
      return (
        <SectionContainer title={l10n.clinicalNotes.progress}>
          <AppointmentAssessments assessments={sessionNotesView?.notes.assessments ?? []} mb={2} />
          <Typography>{sessionNotesView?.followUpProgressNote?.barrierToGoals ?? l10n.clinicalNotes.patientProgressTowardsGoals(patient?.firstName ?? '')}</Typography>
          <LabelColonValue my={2} label={l10n.clinicalNotes.treatmentGoals} value={sessionNotesView?.followUpProgressNote?.treatmentGoals ?? ''} />
          <LabelColonValue my={2} label={l10n.clinicalNotes.recommendation} value={sessionNotesView?.followUpProgressNote.recommendation ?? ''} />
          <LabelColonValue my={2} label={l10n.clinicalNotes.referrals} value={sessionNotesView?.followUpProgressNote.referrals ?? ''} />
          {followUpJSX}
        </SectionContainer>
      );
    }
  };

  const renderRiskAssessmentContent: RenderRiskAssessmentContentFunctionType = (sessionNotesView) => {
    if (sessionNotesView?.notes.riskAssessmentOpted) {
      return (
        <Markdown>
          {sessionNotesView?.riskAssessmentNote.summary}
        </Markdown>
      );
    } else {
      return <Typography>{l10n.clinicalNotes.clientDeniesRisk}</Typography>;
    }
  };

  return (
    <Fragment>
      {sessionNotesView?.presentingProblems.problems && (
        <SectionContainer title={l10n.clinicalNotes.presentingProblems}>
          <Typography whiteSpace="pre-wrap">{sessionNotesView?.presentingProblems.problems}</Typography>
        </SectionContainer>
      )}

      {sessionNotesView?.notes.additionalDetails && (
        <SectionContainer title={l10n.clinicalNotes.clinicalSummary}>
          <Typography whiteSpace="pre-wrap">{sessionNotesView?.notes.additionalDetails}</Typography>
        </SectionContainer>
      )}

      <SectionContainer title={l10n.clinicalNotes.mentalStatus}>
        <Typography>{sessionNotesView?.mentalStatusExamNote}</Typography>
      </SectionContainer>

      <SectionContainer title={l10n.clinicalNotes.riskAssessment}>
        {renderRiskAssessmentContent(sessionNotesView)}
      </SectionContainer>

      <SectionContainer title={l10n.clinicalNotes.treatment}>
        <Box display="flex" gap="1rem" flexDirection="column">
          {sessionNotesView?.treatmentApproachNote.map((note) => {
            // arbitrary slice indexing
            // each treatment note is different, so this works
            return <Typography key={note.slice(0, 10)}>{note}</Typography>;
          })}
        </Box>
        <LabelColonValue mb={0} mt={2} label={l10n.clinicalNotes.homework} value={sessionNotesView?.notes.treatment.homework || l10n.clinicalNotes.none} />
      </SectionContainer>

      {renderProgress(sessionNotesView, followUpJSX)}

      <SessionDetailsSection session={session} appointment={appointment} state={state} patientId={patientId} />
    </Fragment>
  );
};

export const NoteModalBody: React.FC<ClinicalNotesModalRawProps> = ({ state, patientId, children }) => {
  const { data: patientProfile } = useUserProfileQuery(patientId);
  const { classes } = useStyles();
  const { appointment } = state;
  const patientName = `${appointment.patient.firstName} ${appointment.patient.lastName}`;

  return (
    <Box mt={3} className="fs-exclude">
      <Box mx={3} mb={-1}>
        <Typography variant="h6" className={classes.patientName}>
          {patientName}
          {!!patientProfile?.preferredName && ` (${patientProfile.preferredName})`}
        </Typography>
      </Box>

      {children}
    </Box>
  );
};


const PsychiatryNoteSection: React.VFC<{ title: string; content: string }> = ({ title, content }) => {
  return (
    <SectionContainer title={title}>
      <Markdown>{content}</Markdown>
    </SectionContainer>
  );
};

const AllergiesSection: React.VFC<{ patientId: string }> = ({ patientId }) => {
  const l10n = useL10n();
  const { data } = usePatientAllergiesQuery(patientId, { suspense: true });

  const content = React.useMemo(() => {
    if (data?.length) {
      return data?.map(allergy => (
        <Chip key={allergy.id} label={allergy.name} style={{ borderRadius: 'unset' }} />
      ));
    }

    return <Typography>{l10n.clientProfile.allergies.noAllergies}</Typography>;
  }, [ data ]);

  return (
    <SectionContainer title="Allergies">
      <Box display="flex" gap="16px" flexWrap="wrap">
        {content}
      </Box>
    </SectionContainer>
  );
};

const MedicationSection: React.VFC<{ patientId: string }> = ({ patientId }) => {
  const { data } = usePatientMedicationQuery(patientId, { suspense: true });

  const content = React.useMemo(() => {
    if (data?.homemeds?.length) {
      return data?.homemeds?.map(medication => (
        <Chip key={medication.drugName} label={medication.drugName} style={{ borderRadius: 'unset' }} />
      ));
    }

    return <Typography>No medications on record</Typography>;
  }, [ data ]);

  return (
    <SectionContainer title="Current Medication">
      <Box display="flex" gap="16px" flexWrap="wrap">
        {content}
      </Box>
    </SectionContainer>
  );
};

export const PsychiatryNoteModalBody: React.VFC<{ patientId: string; note: PsychiatryNote }> = ({ note, patientId }) => {
  return (
    <>
      {note.forms.diagnosis && <PsychiatryNoteSection content={note.forms.diagnosis.note} title={psychiatryNoteFormConfigByKey.diagnosis.title} />}
      {note.forms.historyOfPresentingIllness && <PsychiatryNoteSection content={note.forms.historyOfPresentingIllness.note} title={psychiatryNoteFormConfigByKey.historyOfPresentingIllness.title} />}
      {note.forms.psychiatricReviewOfSystem && <PsychiatryNoteSection content={note.forms.psychiatricReviewOfSystem.note} title={psychiatryNoteFormConfigByKey.psychiatricReviewOfSystem.title} />}
      {note.forms.psychiatricHistory && <PsychiatryNoteSection content={note.forms.psychiatricHistory?.note} title={psychiatryNoteFormConfigByKey.psychiatricHistory.title} />}
      {note.forms.socialHistory && <PsychiatryNoteSection content={note.forms.socialHistory?.note} title={psychiatryNoteFormConfigByKey.socialHistory.title} />}
      {note.isInitialSession && patientId && (
        <ErrorBoundary fallbackRender={() => null}>
          <React.Suspense fallback={null}>
            <AllergiesSection patientId={patientId} />
          </React.Suspense>
        </ErrorBoundary>
      )}
      {note.isInitialSession && patientId && (
        <ErrorBoundary fallbackRender={() => null}>
          <React.Suspense fallback={null}>
            <MedicationSection patientId={patientId} />
          </React.Suspense>
        </ErrorBoundary>
      )}
      {note.forms.medicalHistory && <PsychiatryNoteSection content={note.forms.medicalHistory?.note} title={psychiatryNoteFormConfigByKey.medicalHistory.title} />}
      {note.forms.mentalStatusExam && <PsychiatryNoteSection content={note.forms.mentalStatusExam.note} title={psychiatryNoteFormConfigByKey.mentalStatusExam.title} />}
      {note.forms.riskAssessment && <PsychiatryNoteSection content={note.forms.riskAssessment.note} title={psychiatryNoteFormConfigByKey.riskAssessment.title} />}
      {note.forms.physicalReviewOfSystems && <PsychiatryNoteSection content={note.forms.physicalReviewOfSystems.note} title={psychiatryNoteFormConfigByKey.physicalReviewOfSystems.title} />}
      {note.forms.plan && <PsychiatryNoteSection content={note.forms.plan.note} title="Plan" />}
      {note.forms.psychotherapyEvaluationCrisisServices && <PsychiatryNoteSection content={note.forms.psychotherapyEvaluationCrisisServices.note} title={psychiatryNoteFormConfigByKey.psychotherapyEvaluationCrisisServices.title} />}
      {note.forms.treatmentEducation && <PsychiatryNoteSection content={note.forms.treatmentEducation.note} title={psychiatryNoteFormConfigByKey.treatmentEducation.title} />}
      {note.forms.prescribeRefill && <PsychiatryNoteSection content={note.forms.prescribeRefill.note} title={psychiatryNoteFormConfigByKey.prescribeRefill.title} />}
      {note.forms.additionalInformation && <PsychiatryNoteSection content={note.forms.additionalInformation.note} title={psychiatryNoteFormConfigByKey.additionalInformation.title} />}
    </>
  );
};

export const SessionNoteModalBody: React.VFC<ClinicalNotesModalRawProps> = ({ state, session, appointment, ...props }) => {
  const l10n = useL10n();

  if (!state || !session || !appointment) {
    return null;
  }

  if (state.isLegacy) {
    return (
      <NoteModalBody session={session} appointment={appointment} state={state} patientId={props.patientId}>
        <SectionContainer title={l10n.clinicalNotes.treatment}>
          <Typography>{state.session.clinicalNote || l10n.clinicalNotes.noteStoredOffPlatform}</Typography>
        </SectionContainer>
        <SessionDetailsSection session={session} appointment={appointment} state={state} patientId={props.patientId} />
      </NoteModalBody>
    );
  }

  if (state.type === AppointmentType.psychiatry && state.psychiatryNote) {
    return (
      <NoteModalBody session={session} appointment={appointment} state={state} patientId={props.patientId}>
        <PsychiatryNoteModalBody note={state.psychiatryNote} patientId={props.patientId} />
        <SessionDetailsSection session={session} appointment={appointment} state={state} patientId={props.patientId} />
        <AddendumContainer
          session={session}
          patientId={appointment.patientId}
          providerId={appointment.providerId}
          noteTime={{
            start: state.psychiatryNote.startTime ?? appointment.fromDatetime,
            end: state.psychiatryNote.endTime ?? appointment.toDatetime,
          }}
          mentalStatusExamNote={state.psychiatryNote.forms.mentalStatusExam.note}
        />
      </NoteModalBody>
    );
  }

  if (state.type === AppointmentType.therapy) {
    return (
      <NoteModalBody session={session} appointment={appointment} state={state} patientId={props.patientId}>
        <SectionContainer title={l10n.clinicalNotes.diagnosis}>
          <Box display="flex" gap="1rem" flexDirection="column">
            {state.session.diagnosis.map(({ name, id, code }) => {
              return <Typography key={id}>{code}&nbsp;{name}</Typography>;
            })}
            {!state.session.diagnosis.length && <Typography>{l10n.clinicalNotes.none}</Typography>}
          </Box>
        </SectionContainer>
        <ClinicalNoteContent session={session} appointment={appointment} state={state} patientId={props.patientId} />
        <AddendumContainer
          session={session}
          patientId={appointment.patientId}
          providerId={appointment.providerId}
          noteTime={{
            start: state.sessionNotesView?.notes.startTime ?? appointment.fromDatetime,
            end: state.sessionNotesView?.notes.endTime ?? appointment.toDatetime,
          }}
          mentalStatusExamNote={state.sessionNotesView?.mentalStatusExamNote}
        />
      </NoteModalBody>
    );
  }

  return null;
};
